import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, CaretUp, CaretDown } from "@phosphor-icons/react";
import { adminApi } from "./api";
import ImageUpload from "./ImageUpload";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Edits the CMS key `clients.logos` which drives the Trusted-by-Clients marquee.
 * Structure: { heading, sub, logos: [ { name, image_id?, logo_url?, website? } ] }
 */
export default function ClientLogosManager() {
  const [data, setData] = useState({ heading: "", sub: "", logos: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/site-content`);
        const j = await r.json();
        const raw = j?.page?.["clients.logos"];
        const parsed = typeof raw === "string" ? JSON.parse(raw) : (raw || {});
        parsed.logos = parsed.logos || [];
        setData(parsed);
      } catch (e) {
        toast.error("Failed to load client logos");
      }
      setLoading(false);
    })();
  }, []);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const setLogo = (i, k, v) =>
    setData((d) => {
      const logos = [...d.logos];
      logos[i] = { ...logos[i], [k]: v };
      return { ...d, logos };
    });

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= data.logos.length) return;
    const logos = [...data.logos];
    [logos[i], logos[j]] = [logos[j], logos[i]];
    setData({ ...data, logos });
  };

  const remove = (i) => {
    if (!window.confirm("Remove this logo?")) return;
    setData((d) => ({ ...d, logos: d.logos.filter((_, k) => k !== i) }));
  };

  const add = () =>
    setData((d) => ({ ...d, logos: [...d.logos, { name: "New Client", image_id: null, website: "" }] }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/page-content", { key: "clients.logos", value: JSON.stringify(data) });
      toast.success("Client logos saved.");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-[#6B7A8D]">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="client-logos-manager">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-[24px]" style={{ color: "#0F2A6E" }}>Client Logos (marquee)</h1>
          <p className="mt-1 text-[13px] text-[#6B7A8D]">
            {data.logos.length} logo{data.logos.length === 1 ? "" : "s"} · Upload PNG/JPG per client; the marquee auto-loops for seamless scrolling.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button" onClick={add}
            className="h-9 px-4 rounded-md border border-slate-200 text-[12.5px] font-semibold inline-flex items-center gap-1.5"
            style={{ color: "#0F2A6E" }} data-testid="client-logos-add">
            <Plus size={13} weight="bold" /> Add logo
          </button>
          <button
            type="button" onClick={save} disabled={saving}
            className="h-9 px-4 rounded-md text-white text-[12.5px] font-semibold disabled:opacity-60 inline-flex items-center gap-1.5"
            style={{ background: "#1B6FE0" }} data-testid="client-logos-save">
            <FloppyDisk size={13} weight="fill" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Section header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6 space-y-3">
        <div>
          <Label>Section heading</Label>
          <Input value={data.heading || ""} onChange={(e) => setField("heading", e.target.value)} className="mt-1" data-testid="client-logos-heading" />
        </div>
        <div>
          <Label>Sub-line</Label>
          <Textarea rows={2} value={data.sub || ""} onChange={(e) => setField("sub", e.target.value)} className="mt-1" />
        </div>
      </div>

      {/* Logo list */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="client-logos-list">
        {data.logos.map((l, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4 flex gap-4" data-testid={`client-logo-row-${i}`}>
            <div className="w-40 md:w-44 shrink-0">
              <ImageUpload value={l.image_id} onChange={(id) => setLogo(i, "image_id", id)} />
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              <div>
                <Label className="text-[11.5px]">Client name</Label>
                <Input value={l.name || ""} onChange={(e) => setLogo(i, "name", e.target.value)} className="mt-1 text-[13px]" data-testid={`client-logo-name-${i}`} />
              </div>
              <div>
                <Label className="text-[11.5px]">Website (optional)</Label>
                <Input value={l.website || ""} onChange={(e) => setLogo(i, "website", e.target.value)} className="mt-1 text-[12px]" placeholder="https://…" />
              </div>
              <div className="flex items-center justify-end gap-1 pt-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-7 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center" data-testid={`client-logo-up-${i}`}>
                  <CaretUp size={11} weight="bold" />
                </button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === data.logos.length - 1} className="w-7 h-7 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center" data-testid={`client-logo-down-${i}`}>
                  <CaretDown size={11} weight="bold" />
                </button>
                <button type="button" onClick={() => remove(i)} className="w-7 h-7 rounded border border-red-200 hover:bg-red-50 text-red-600 inline-flex items-center justify-center" data-testid={`client-logo-remove-${i}`}>
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
